import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { Action } from '@interstate/components/Action';
import { ArrowTopRightOnSquareIcon } from '@interstate/components/Icons';
import type { BadgeVariant } from '@interstate/components/Badge';
import { Box } from '@interstate/components/Box';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Grid } from '@interstate/components/Grid';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import { TextInput } from '@interstate/components/TextInput';
import { Typography } from '@interstate/components/Typography';

import { BreakPoint } from '@makemydeal/dr-activities-common';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import {
    dealLockHooks,
    FieldUpdatingSpinner,
    FlexRow,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    StyledVAutoAppraisalStatus,
    usePaymentCall,
    useFieldUpdatingIndicator,
    useVAutoAppraisalLaunch
} from '@makemydeal/dr-dash-components';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';

import { VAutoAppraisalStatus } from '@makemydeal/dr-dash-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { tradeInContainerGridProps, tradeInContainerLargeFieldsProps } from '../lienholderForm/utils';
import type { LienholderFormData } from '../../../types';
import { LienholderField } from '../lienholderForm/LienholderField';
import { useSingleTradeInFormProvider } from './TradeInFormProvider';
import { NumericInput } from '@interstate/components/NumericInput';
import { ACV, ALLOWANCE, PAYOFF } from '../../../constants';

interface MultiTradeInCollapsedViewProps {
    isEditDisabled: boolean;
    tradeInIndex: number;
    lienholderData: LienholderFormData;
    setLienholderData: React.Dispatch<React.SetStateAction<LienholderFormData>>;
    isTradeInFormDirtyRef?: React.MutableRefObject<boolean>;
}

export const MultiTradeInCollapsedView: React.FC<MultiTradeInCollapsedViewProps> = ({
    isEditDisabled,
    tradeInIndex,
    lienholderData,
    setLienholderData,
    isTradeInFormDirtyRef
}) => {
    const dispatch = useDispatch();

    const scopeId = useSelector((state: any) => scopedStateSelectors.getCurrentScopeId(state));
    const shouldIncludeTradeInWithOffer = useSelector(tradeInsSelectors.getShouldIncludeTradeIn(tradeInIndex));
    const isTradeInCompleted = useSelector(tradeInSelectors.isTradeInCompleted);
    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));

    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isCompactUI = useSelector(baseDealSelectors.getCurrentSlideOut) === baseDealActionCreators.SlideOutType.TradeIn;

    const isVAutoAppraisalEnabled = useSelector(dealerSelectors.isVAutoAppraisalEnabled);
    const isTradeAcvOverrideEnabled = useSelector(dealerSelectors.isTradeAcvOverrideEnabled);
    const currAppraisal = useSelector(vAutoSelectors.getCurrentAppraisalByVin(vin));
    const vAutoAppraisalStatus = currAppraisal?.status;
    const vAutoAppraisalAcv = currAppraisal?.value;
    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isMobile = useMediaQuery({ query: `(max-width: ${BreakPoint.PHONE})` });
    const isLargeScreen = useMediaQuery({ query: `(min-width: 1360px)` });
    const failedAllowance = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ALLOWANCE_CHANGED));
    const failedPayoff = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.PAYOFF_CHANGED));
    const failedACV = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ACV_CHANGED));
    const tradeInputsDisabled = isEditDisabled;
    const isTradeInDeleted = useSelector(tradeInsSelectors.isTradeInDeleted(tradeInIndex));
    const isLocked = dealLockHooks.useIsDealLocked();
    // An appraisal refresh can overwrite the ACV, and that update does not come from this field.
    const isRefreshingAppraisals = useSelector(vAutoSelectors.getIsRefreshingAppraisals);
    const { launchAppraisal, keepFocusOnMouseDown } = useVAutoAppraisalLaunch(tradeInIndex);

    const { softSaveLienHolderData: providerSoftSaveLienHolderData, tradeInFormErrors } = useSingleTradeInFormProvider();

    const { xs, sm, md, lg, xl } = tradeInContainerGridProps(
        isBreakdownOpen,
        isLargeScreen,
        isDealCentralMultiScenario,
        isCompactUI
    );
    const largeFieldProps = tradeInContainerLargeFieldsProps(
        isBreakdownOpen,
        isLargeScreen,
        isDealCentralMultiScenario,
        isCompactUI
    );

    const currencyInputProps = {
        inputPrefix: '$',
        autoInsertCommas: true,
        allowDecimal: true,
        decimalMaxLength: 2,
        decimalMinLength: 0,
        placeholder: '10,000.00',
        size: 'sm' as const
    };

    const includeTradeCheckboxValue = shouldIncludeTradeInWithOffer ? 'includeTrade' : '';

    const isCompactLayout = isLargeScreen && !isBreakdownOpen && !isDealCentralMultiScenario;

    /* istanbul ignore next */
    const {
        value: allowance,
        handleKeyDown: allowanceKeyDown,
        handleChange: changeAllowance,
        handleBlur: allowanceBlur
    } = usePaymentCall(tradeInsSelectors.getTradeValue(tradeInIndex), tradeInsActionCreators.allowanceChanged(tradeInIndex), {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const {
        value: payoff,
        handleKeyDown: payoffKeyDown,
        handleChange: changePayoff,
        handleBlur: payoffBlur
    } = usePaymentCall(tradeInsSelectors.getTradeInAmountOwed(tradeInIndex), tradeInsActionCreators.payoffChanged(tradeInIndex), {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const { actionCreator: trackedAcvAction, isUpdating: isAcvUpdating } = useFieldUpdatingIndicator(
        tradeInsActionCreators.acvChanged(tradeInIndex)
    );
    const {
        value: acv,
        handleKeyDown: acvKeyDown,
        handleChange: changeAcv,
        handleBlur: acvBlur
    } = usePaymentCall(tradeInsSelectors.getTradeACV(tradeInIndex), trackedAcvAction, {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const hasLienholderErrors = useMemo(() => {
        const lienholderErrors = (tradeInFormErrors as any)?.lienholder;
        return Boolean(
            lienholderErrors?.name?.hasError ||
                lienholderErrors?.accountNumber?.hasError ||
                lienholderErrors?.streetAddress?.hasError ||
                lienholderErrors?.city?.hasError ||
                lienholderErrors?.state?.hasError ||
                lienholderErrors?.zip?.hasError
        );
    }, [tradeInFormErrors]);

    const softSaveLienHolderData = () => {
        if (hasLienholderErrors) {
            if (isTradeInFormDirtyRef) {
                isTradeInFormDirtyRef.current = true;
            }
            return;
        }
        providerSoftSaveLienHolderData(lienholderData);
        if (isTradeInFormDirtyRef) {
            isTradeInFormDirtyRef.current = false;
        }
    };

    const getAppraisalStatusTitle = useMemo(() => {
        if (!isVAutoAppraisalEnabled) {
            return null;
        }

        if (vAutoAppraisalStatus) {
            let variant: BadgeVariant = 'info';
            let message = '';

            if (vAutoAppraisalStatus === VAutoAppraisalStatus.InProcess) {
                variant = 'caution';
                message = 'Appraisal in Progress';
            } else if (vAutoAppraisalStatus === VAutoAppraisalStatus.Completed) {
                variant = 'success';
                message = 'Appraised';
            }

            return (
                <Typography variant="h4">
                    <StyledVAutoAppraisalStatus data-testid="vauto-status-badge" variant={variant}>
                        {message}
                    </StyledVAutoAppraisalStatus>
                </Typography>
            );
        }

        const isDisabledLink = !vin || isEditDisabled;
        return (
            // The mousedown sits on a wrapper because Action does not type the prop; `display:
            // contents` keeps the Action itself as the laid-out element.
            <span style={{ display: 'contents' }} onMouseDown={keepFocusOnMouseDown}>
                <Action
                    data-testid="vAuto-link"
                    actionIcon={{
                        icon: <ArrowTopRightOnSquareIcon />,
                        ...(isMobile ? { end: true } : { start: true })
                    }}
                    onClick={launchAppraisal}
                    disabled={isDisabledLink}
                >
                    <Typography
                        variant="anchor-block-sm"
                        tag={isDisabledLink ? 'div' : 'a'}
                        color={isDisabledLink ? 'base.color.gray.400' : 'base.color.blue.700'}
                    >
                        Start Appraisal
                    </Typography>
                </Action>
            </span>
        );
    }, [isVAutoAppraisalEnabled, vAutoAppraisalStatus, isMobile, launchAppraisal, keepFocusOnMouseDown, isEditDisabled, vin]);

    const handleIncludeTradeWithOfferChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        const isTradeInIncluded = Boolean(event.target.value.checked);
        dispatch(tradeInsActionCreators.includeTradeChanged(isTradeInIncluded, tradeInIndex));
        dispatch(interactionAnalyticsActionCreators.tradeIncludeWithOfferCheckbox());
    };

    const displayAcv = !isTradeAcvOverrideEnabled && vAutoAppraisalAcv > 0 ? formatNumberAndCents(vAutoAppraisalAcv) : acv;

    return (
        <Box
            data-testid={`multi-trade-collapsed-view-${tradeInIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            onKeyUp={(e: React.KeyboardEvent) => e.stopPropagation()}
            marginLeft={-3}
            marginRight={-2}
        >
            <Grid container columnSpacing={isDealCentralMultiScenario ? 1 : { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }} rowSpacing={2}>
                <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                    <TextInput
                        data-testid="vin-code"
                        label="VIN"
                        sx={{ whiteSpace: 'normal', overflow: 'hidden', wordBreak: 'break-all', height: 'auto' }}
                        name={`collapsed-vin-display-${tradeInIndex + 1}`}
                        value={vin}
                        readOnly
                        size="sm"
                    />
                </Grid>

                <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                    <NumericInput
                        {...currencyInputProps}
                        data-testid="allowance"
                        label={ALLOWANCE}
                        name={kebabCase(ALLOWANCE)}
                        onChange={changeAllowance}
                        value={allowance}
                        hasError={failedAllowance}
                        errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                        onBlur={allowanceBlur}
                        onKeyDown={allowanceKeyDown}
                        disabled={tradeInputsDisabled}
                    />
                </Grid>
                <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                    <NumericInput
                        {...currencyInputProps}
                        data-testid="acv"
                        inputSuffix={
                            isAcvUpdating || isRefreshingAppraisals ? (
                                <FieldUpdatingSpinner testId="acv-updating-indicator" />
                            ) : undefined
                        }
                        label={ACV}
                        name={kebabCase(ACV)}
                        onChange={changeAcv}
                        value={displayAcv}
                        onBlur={acvBlur}
                        onKeyDown={acvKeyDown}
                        hasError={failedACV}
                        errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                        disabled={tradeInputsDisabled || (!!vAutoAppraisalStatus && !isTradeAcvOverrideEnabled)}
                    />
                </Grid>
                <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                    <NumericInput
                        {...currencyInputProps}
                        data-testid="payoff"
                        label={PAYOFF}
                        name={kebabCase(PAYOFF)}
                        onChange={changePayoff}
                        value={payoff}
                        hasError={failedPayoff}
                        errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                        onBlur={payoffBlur}
                        onKeyDown={payoffKeyDown}
                        disabled={tradeInputsDisabled}
                    />
                </Grid>
                <Grid {...largeFieldProps}>
                    <LienholderField
                        lienholderData={lienholderData}
                        setLienholderData={setLienholderData}
                        isEditDisabled={isEditDisabled}
                        softSaveLienHolderData={softSaveLienHolderData}
                        isTradeInCompleted={isTradeInCompleted}
                        renderDropdownPortal={true}
                    />
                </Grid>

                <Grid xs={12} sm={12} md={12} lg={12} xl={isCompactLayout ? 3 : 12} mt={isCompactLayout ? 3 : 0}>
                    <FlexRow justifyContent={'space-between'}>
                        <CheckBox
                            id={`include-trade-checkbox-${scopeId}-${tradeInIndex}`}
                            name={`include-trade-with-offer-${scopeId}-${tradeInIndex}`}
                            checked={shouldIncludeTradeInWithOffer}
                            label={'Include trade with offer'}
                            value={includeTradeCheckboxValue}
                            data-testid={`include-trade-with-offer-checkbox-${tradeInIndex}`}
                            onChange={handleIncludeTradeWithOfferChange}
                            sx={{ backgroundColor: 'transparent' }}
                            disabled={isTradeInDeleted || isLocked}
                        />
                        {getAppraisalStatusTitle}
                    </FlexRow>
                </Grid>
            </Grid>
        </Box>
    );
};
